/*
 * useImportSelectionBridge
 *
 * MainComponent side effects after a successful route import: select the
 * imported route, mark its vessel (the reducer clears the previous vessel's
 * selections), recenter the map to the vessel's position on the route, and
 * publish the tree import hint.
 */

import { useCallback } from "react";
import { ESTIMATED_START, ESTIMATED_START_MESSAGE } from "@/features/route/recalcStart";

export default function useImportSelectionBridge({
  addNotification,
  setSwitchVesselFiltersActive,
  setImportSelectionHint,
  setMarkedVesselNames,
  recenterToPoint,
  refetchPlanRoutes,
}) {
  const handleImportSuccess = useCallback(
    ({
      routeId,
      vesselId,
      vesselName,
      voyageId,
      voyageName,
      routeName,
      routeType,
      state,
      voyageActivated,
      timestamp,
      summary,
      optimisedFor,
      analysed = true,
      analysisError,
      skippedWaypoints = 0,
      recenterLonLat,
      source = "import",
      archivedVoyageName = null,
    }) => {
      const label = routeName || `Route ${routeId ?? ""}`;
      addNotification?.({
        message: source === "planner" ? `Saved route: ${label}` : `Imported route: ${label}`,
        type: "success",
      });
      if (archivedVoyageName != null) {
        addNotification?.({
          message: archivedVoyageName
            ? `Voyage '${archivedVoyageName}' archived.`
            : "Previous active voyage archived.",
          type: "info",
        });
      }
      if (!analysed) {
        addNotification?.({
          message: `Imported without weather analysis: ${analysisError || "analysis failed"}`,
          type: "warning",
        });
      } else if (summary?.start_source === ESTIMATED_START) {
        addNotification?.({ message: ESTIMATED_START_MESSAGE, type: "info" });
      } else if (skippedWaypoints > 0) {
        addNotification?.({
          message: `Imported from the last reported position: ${skippedWaypoints} past ${skippedWaypoints === 1 ? "waypoint" : "waypoints"} skipped`,
          type: "info",
        });
      }

      // The DB demoted routes (voyage cascade) — refresh the fleet-plan
      // snapshot so the old active route stops rendering on the map.
      refetchPlanRoutes?.();

      const normalizedRouteId = Number(routeId);
      if (Number.isFinite(normalizedRouteId)) {
        // The import-hint effect selects the route in the tree reducer.
        setSwitchVesselFiltersActive(true);
      }

      setImportSelectionHint({
        routeId: Number.isFinite(normalizedRouteId) ? normalizedRouteId : null,
        voyageId: Number.isFinite(Number(voyageId)) ? Number(voyageId) : null,
        vesselId: Number.isFinite(Number(vesselId)) ? Number(vesselId) : null,
        vesselName: vesselName || "",
        voyageName: voyageName || "",
        routeName: routeName || "",
        routeType: routeType || "planned",
        state: state || "suggested",
        voyageActivated: Boolean(voyageActivated),
        timestamp: timestamp || null,
        summary: summary ?? null,
        optimisedFor: optimisedFor ?? null,
      });

      if (vesselName) {
        // Atomic intent: a vessel change clears the previous selections in
        // the same reducer step; re-marking the same vessel keeps them (an
        // unsaved draft of that vessel survives the import).
        setMarkedVesselNames([{ vesselName, vesselId: vesselId ?? null }]);
      }

      if (Array.isArray(recenterLonLat) && recenterLonLat.length === 2) {
        recenterToPoint?.([recenterLonLat[1], recenterLonLat[0]]);
      }
    },
    [
      addNotification,
      setImportSelectionHint,
      setMarkedVesselNames,
      setSwitchVesselFiltersActive,
      recenterToPoint,
      refetchPlanRoutes,
    ],
  );

  return {
    handleImportSuccess,
  };
}
